/**
 * MONSTER TOWN — FEEDING TIME. ZOO CREW (src/venues/zoo/**).
 *
 * The zoo's run-mode, and the best real learning content in the game:
 *
 *   PICK  every animal actually standing in this zoo, as a picture. If the kid
 *         is carrying a cooked dish there is a big card above them — that is the
 *         reference errand's last step, and it feeds EVERYBODY at once.
 *   FEED  one animal, huge, with four foods underneath. One of them is what it
 *         eats. Get it right and the animal is delighted, makes its noise, and
 *         pays you. Get it wrong and it giggles and shakes its head, and you can
 *         try again immediately — nothing is used up, nothing is lost, there is
 *         no score and no timer. Its home and its noise are on the same card, so
 *         the three questions (what does it eat / where does it live / what does
 *         it say) are all answerable from one screen.
 *
 * THE FOOD IS THE ZOO'S, NOT THE KID'S. Feeding never takes anything out of the
 * bag, because a kid with an empty bag must never be locked out of the best
 * thing in the game — the trough is full, and it stays full (LAW #2). Carrying
 * the right food is a bonus, not a toll.
 *
 * Built on `panelShell()` in this crew's own layer, for the same reason the
 * kitchen is: `src/ui/**` belongs to another crew, and `ui:panel` is enough to
 * keep exactly one panel open at a time across both.
 */

import './zoo.css'
import { bus } from '../../core/events'
import { engine } from '../../core/engine'
import { clock } from '../../core/time'
import { wallet } from '../../core/economy'
import { inventory } from '../../core/inventory'
import { collection } from '../../core/collection'
import { save } from '../../core/save'
import { world } from '../../core/scene'
import type { InstanceId, SceneId } from '../../core/types'
import { app } from '../../app/context'
import { clear, dots, el, onTap } from '../../ui/dom'
import { iconEl } from '../../ui/icons'
import { panelShell } from '../../ui/panels'
import { picEl } from '../../cooking/pics'
import { allRecipes, lookOf } from '../../cooking/types'
import { ANIMALS, ZOO_SCENE, animalByDef, type AnimalDef } from '../../content/animals'
import { animalEl, habitatEl, soundEl } from './animalPic'

export const ZOO_PANEL = 'zoofeed'

type View = 'pick' | 'feed'

let layer: HTMLElement | null = null
let view: View = 'pick'
let chosen: AnimalDef | null = null
/** animal id -> the in-world instance, so a correct feed makes the REAL one hop. */
const instanceOf = new Map<string, InstanceId>()
/** animal id -> in-world day it was last fed. Purely so the coin is once a day. */
const fedOn = new Map<string, number>()

export function zooSheetOpen(): boolean {
  return layer !== null && layer.childElementCount > 0
}

/** QA surface. Never shown to a kid. */
export function zooSheetState(): { open: boolean; view: View; animal: string | null; here: string[] } {
  return { open: zooSheetOpen(), view, animal: chosen?.id ?? null, here: animalsHere().map((a) => a.id) }
}

// ---------------------------------------------------------------------------
// what is in the room
// ---------------------------------------------------------------------------

/** Every animal placed in the zoo right now, in placement order. */
export function animalsHere(): AnimalDef[] {
  const node = world.get(ZOO_SCENE)
  instanceOf.clear()
  if (node === undefined) return []
  const out: AnimalDef[] = []
  for (const p of node.grid.all()) {
    const a = animalByDef(String(p.defId))
    if (a === undefined) continue
    if (!instanceOf.has(a.id)) instanceOf.set(a.id, p.instanceId)
    if (!out.some((x) => x.id === a.id)) out.push(a)
  }
  return out
}

/** Dishes the kid is carrying. The reference errand's payload lives in here. */
function dishesCarried(): string[] {
  return allRecipes()
    .map((r) => r.dish)
    .filter((d) => inventory.count(d) > 0)
}

/**
 * Four foods: the right one and three decoys, shuffled with the ENGINE's seeded
 * rng so a screenshot run is reproducible and `Math.random` never appears.
 */
function foodChoices(a: AnimalDef): string[] {
  const others = ANIMALS.filter((x) => x.food !== a.food).map((x) => x.food)
  const pool = [...new Set(others)]
  const rng = engine.rng
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const t = pool[i] as string
    pool[i] = pool[j] as string
    pool[j] = t
  }
  const picked = [a.food, ...pool.slice(0, 3)]
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const t = picked[i] as string
    picked[i] = picked[j] as string
    picked[j] = t
  }
  return picked
}

function foodEl(itemId: string, size: number): HTMLSpanElement {
  const l = lookOf(itemId)
  return picEl(l.pic, { color: l.color, accent: l.accent }, size)
}

function wobble(node: HTMLElement): void {
  node.dataset['wobble'] = '1'
  window.setTimeout(() => delete node.dataset['wobble'], 420)
}

/** Make the REAL animal in the room react, not just the card. */
function cheerInWorld(a: AnimalDef): void {
  const inst = instanceOf.get(a.id)
  if (inst !== undefined) {
    bus.emit('prop:anim', { instanceId: inst, clip: 'bounce', seconds: 1.1, target: 'body' })
    bus.emit('prop:anim', { instanceId: inst, clip: 'wobble', seconds: 0.9, target: 'head' })
  }
  bus.emit('fx:play', { effect: 'hearts', at: { ...app.playerPos }, count: 10 })
  bus.emit('audio:cue', { cue: a.sound })
}

// ---------------------------------------------------------------------------
// views
// ---------------------------------------------------------------------------

function buildPick(body: HTMLElement): void {
  const dishes = dishesCarried()
  if (dishes.length > 0) {
    // THE ERRAND'S LAST STEP, and it is the biggest thing on the screen.
    const card = el('div', 'mt-zoo-deliver')
    card.setAttribute('role', 'button')
    card.setAttribute('aria-label', 'Give the dish to the animals')
    const first = dishes[0] as string
    card.append(foodEl(first, 120))
    const arrow = el('span', 'mt-zoo-arrow')
    arrow.append(iconEl('heart', 56))
    card.append(arrow)
    const faces = el('div', 'mt-zoo-faces')
    for (const a of animalsHere().slice(0, 4)) faces.append(animalEl(a, 76))
    card.append(faces)
    onTap(card, () => deliverDish(first), null)
    body.append(card)
  }

  const here = animalsHere()
  const grid = el('div', 'mt-grid mt-zoo-grid')
  for (const a of here) {
    const card = el('div', 'mt-card mt-zoo-card')
    card.setAttribute('role', 'button')
    card.setAttribute('aria-label', a.devName)
    card.append(animalEl(a, 112))
    /*
     * THE THREE ANSWERS, ON THE CARD, FOR THIS ANIMAL (fixed vr1 — MAJOR).
     *
     * This row used to be `iconEl('dinner')` and `iconEl('who')` — two generic
     * pictures from the shared HUD set, IDENTICAL on all four cards. Critic:
     * *"their whole learning payload is three identical pips … a ~34px food bowl
     * and two ~30px coloured discs per card — unreadable at iPad viewing
     * distance — and, critically, THE TWO DISCS ARE THE SAME ON ALL FOUR CARDS.
     * Nothing on the feeding sheet differentiates any animal's habitat or sound
     * from any other's."* That is the brief's entire claim for venue #3 ("each
     * animal eats different food, wants a different habitat, and makes a
     * different sound") rendered as wallpaper.
     *
     * All three are now drawn from the animal's OWN data row — its `food` item,
     * its `habitat`, its `sound` cue — at 54 px instead of 30, which is the size
     * the same pictures already use on the feed screen. Nobody had to draw
     * anything: `foodEl` and `habitatEl` existed, and `soundEl` learned to draw
     * the noise (see animalPic.ts). A new species still brings its own row and
     * gets its own three pictures for free.
     */
    const row = el('div', 'mt-zoo-row')
    row.append(foodEl(a.food, 54))
    row.append(habitatEl(a.habitat, 54))
    row.append(soundEl(a.sound, 54))
    card.append(row)
    if (collection.has(a.id)) {
      const known = el('span', 'mt-zoo-known')
      known.append(iconEl('star', 30))
      card.append(known)
    }
    onTap(card, () => {
      chosen = a
      view = 'feed'
      repaint()
    })
    grid.append(card)
  }
  if (here.length === 0) {
    // An empty zoo is not an error state — it is an invitation to go and build
    // one, said with the hammer, which is the button that does it.
    const empty = el('div', 'mt-zoo-empty')
    empty.append(iconEl('hammer', 96))
    empty.append(iconEl('monster', 96))
    body.append(empty)
  }
  body.append(grid)
  /*
   * NO SECOND WAY OUT (fixed vr2 — MAJOR). This foot used to carry a bare
   * multiplication sign in a cream square, at the bottom of the screen, doing
   * exactly what the cream back-chevron in the corner already does. Critic:
   * *"the sheet offers two different ways out with two different shape
   * languages … every other panel in the game leaves by the chevron."* A
   * pre-reader learns ONE symbol for "back" and it is the chevron `panelShell`
   * puts on every panel in the game before the panel's own content exists, so
   * the sheet now leaves the way the shop, the kitchen and the book leave.
   */
}

function buildFeed(body: HTMLElement): void {
  const a = chosen
  if (a === null) {
    view = 'pick'
    buildPick(body)
    return
  }

  const stage = el('div', 'mt-zoo-stage')
  stage.append(animalEl(a, 200))
  stage.dataset['happy'] = 'false'
  body.append(stage)

  // Where it lives, and what it says. Both tappable, both pure delight.
  const facts = el('div', 'mt-zoo-facts')
  const home = el('button', 'mt-btn big mt-zoo-fact')
  home.type = 'button'
  home.setAttribute('aria-label', `Where ${a.devName} lives`)
  home.append(habitatEl(a.habitat, 60))
  onTap(home, () => {
    bus.emit('fx:play', { effect: 'sparkle', at: { ...app.playerPos }, count: 5 })
  })
  const say = el('button', 'mt-btn big mt-zoo-fact')
  say.type = 'button'
  say.setAttribute('aria-label', `What ${a.devName} says`)
  say.append(soundEl(a.sound, 60))
  onTap(
    say,
    () => {
      bus.emit('audio:cue', { cue: a.sound })
      cheerInWorld(a)
    },
    null,
  )
  facts.append(home, say)
  body.append(facts)

  // The four foods. This is the question.
  const tray = el('div', 'mt-zoo-tray')
  for (const item of foodChoices(a)) {
    const b = el('button', 'mt-btn big mt-zoo-food')
    b.type = 'button'
    b.setAttribute('aria-label', item)
    b.append(foodEl(item, 62))
    const have = inventory.count(item)
    if (have > 0) b.append(dots(Math.min(6, have), Math.min(6, have)))
    onTap(b, () => offerFood(a, item, b, stage), null)
    tray.append(b)
  }
  body.append(tray)
  // Same rule as `buildPick`: the chevron in the corner is the way back to the
  // other animals, and it is the only one. See the note there.
}

// ---------------------------------------------------------------------------
// doing things
// ---------------------------------------------------------------------------

/**
 * THE WRONG FOOD IS A GIGGLE. The animal shakes its head, the button wobbles,
 * and the very next tap can be right. Nothing is consumed, nothing is scored,
 * and the card does not close — the kid stays exactly where they were.
 */
function offerFood(a: AnimalDef, item: string, node: HTMLElement, stage: HTMLElement): void {
  if (item !== a.food) {
    wobble(node)
    stage.dataset['shake'] = '1'
    window.setTimeout(() => delete stage.dataset['shake'], 500)
    bus.emit('audio:cue', { cue: 'giggle' })
    return
  }

  stage.dataset['happy'] = 'true'
  cheerInWorld(a)
  bus.emit('fx:play', { effect: 'confetti', at: { ...app.playerPos }, count: 10 })
  bus.emit('audio:cue', { cue: 'chime_happy' })
  bus.emit('monster:action', { action: 'cheer', seconds: 1.2 })

  // Carrying the right food is a BONUS, never a toll: if it is in the bag it is
  // used and pays double; if it is not, the zoo's own basket feeds the animal.
  const fromBag = inventory.take(item, 1, 'fed the animals')
  const day = clock.day
  const first = fedOn.get(a.id) !== day
  fedOn.set(a.id, day)
  const coins = first ? a.coins * (fromBag ? 2 : 1) : 0
  if (coins > 0) wallet.earnExact(coins, 'zoo.fed', { ...app.playerPos })

  if (!collection.has(a.id)) collection.found(a.id, { at: { ...app.playerPos } })
  save.request('zoo:fed')

  /*
   * LET THEM ENJOY IT (vr1). *"The correct pick bounces the kid straight back to
   * the pick grid rather than letting them enjoy the animal."* 900 ms is shorter
   * than the confetti, shorter than the animal's own noise, and shorter than the
   * `happy` pose it just went into — so the reward for getting it right was the
   * screen being taken away mid-celebration. There is nothing to hurry back to;
   * the grid is one tap either way (LAW #2). The kid can still leave at any
   * moment with the back button, which is the control that has always been there.
   */
  window.setTimeout(() => {
    view = 'pick'
    chosen = null
    repaint()
  }, 2400)
}

/**
 * THE END OF THE REFERENCE ERRAND.
 *
 * "…carry it across town and feed the animals — they do a happy animation and
 * you're paid and thanked." All three of those happen here, and the observation
 * that ticks the errand's `deliver` step is emitted here too, because a deliver
 * is one of the three step kinds the errand engine cannot adapt for itself.
 */
export function deliverDish(dishId: string): boolean {
  if (!inventory.take(dishId, 1, 'fed the animals')) return false
  const here = animalsHere()

  for (const a of here) cheerInWorld(a)
  bus.emit('fx:play', { effect: 'confetti', at: { ...app.playerPos }, count: 16 })
  bus.emit('fx:play', { effect: 'hearts', at: { ...app.playerPos }, count: 12 })
  bus.emit('audio:cue', { cue: 'chime_happy' })
  bus.emit('monster:action', { action: 'cheer', seconds: 1.6 })

  // THE WIRE. `to: 'zoo_feeder'` is what the errand's deliver step names.
  bus.emit('errand:observe', { kind: 'deliver', what: dishId, count: 1, to: 'zoo_feeder' })

  // Paid and thanked, whether or not an errand happened to be running: feeding
  // hungry animals is a good deed on its own. Deliberately modest — the errand
  // crew pays the errand's own reward on `errand:finished`, and this is the
  // zoo's thank-you on top of it, not a second wage.
  const paid = 20 + here.length * 3
  wallet.earnExact(paid, 'zoo.delivered', { ...app.playerPos })
  for (const a of here) if (!collection.has(a.id)) collection.found(a.id, { at: { ...app.playerPos } })
  save.request('zoo:delivered')

  view = 'pick'
  chosen = null
  repaint()
  return true
}

// ---------------------------------------------------------------------------
// the layer
// ---------------------------------------------------------------------------

function repaint(): void {
  if (layer === null) return
  clear(layer)
  const parts = panelShell({
    icon: 'monster',
    onBack: () => {
      if (view === 'feed') {
        view = 'pick'
        chosen = null
        repaint()
      } else close()
    },
  })
  parts.root.classList.add('mt-zoo')
  if (view === 'pick') buildPick(parts.body)
  else buildFeed(parts.body)
  layer.append(parts.root)
}

/**
 * ==========================================================================
 *  THE HUD HAS TO GET OFF THE GLASS (fixed vr2 — MAJOR)
 * ==========================================================================
 * The sheet is a full-screen panel and it never said so. `ui:panel` was only
 * ever emitted on CLOSE, so `#hud[data-panel]` stayed at '0' the whole time the
 * sheet was up and every rule in the `#hud[data-panel='1']` block in ui.css —
 * the one that takes the purse, the left rail, the camera cluster and the
 * signposts off the screen for a panel — simply did not fire. Photographed:
 * the six-button rail drawn into the sheet's top-left card, the coin purse
 * poking out from behind the back chevron, three z-levels stacked in one 200px
 * corner. It also left the collection book free to open ON TOP of the sheet,
 * because a layer that never announces itself cannot ask anything to move.
 *
 * The cooking crew hit exactly this and solved it exactly this way, and the
 * shape is copied verbatim on purpose (see `announceOpen` in
 * `src/cooking/sheet.ts`): emit twice, because the FIRST emit makes every other
 * sheet close itself and a sheet closing answers with `ui:panel { panel: null }`
 * — which would leave the HUD believing nothing is open and paint its chrome
 * straight back over ours. The second lands after they have all gone quiet and
 * is a no-op for every listener. Our own listener ignores both (`announcing`).
 */
let announcing = false
function announceOpen(): void {
  if (announcing) return
  announcing = true
  try {
    bus.emit('ui:panel', { panel: ZOO_PANEL })
    bus.emit('ui:panel', { panel: ZOO_PANEL })
  } finally {
    announcing = false
  }
}

export function openZooSheet(): void {
  if (layer === null) return
  if (!zooSheetOpen()) {
    view = 'pick'
    chosen = null
  }
  repaint()
  bus.emit('audio:cue', { cue: 'ui_open' })
  announceOpen()
}

export function close(): void {
  if (layer === null || !zooSheetOpen()) return
  clear(layer)
  view = 'pick'
  chosen = null
  bus.emit('audio:cue', { cue: 'ui_close' })
  bus.emit('ui:panel', { panel: null })
}

/** Called once by `installZoo`. */
export function installZooSheet(hud: HTMLElement): void {
  if (layer !== null) return
  layer = el('div', 'mt-zoo-layer')
  hud.append(layer)

  bus.on('ui:panel', (e) => {
    if (announcing) return // our own "I am open" — see `announceOpen()`
    if (e.panel === ZOO_PANEL) openZooSheet()
    else if (e.panel !== null) close()
  })
  bus.on('scene:transitionStart', () => close())
}

/** Which scene the sheet belongs to. Exported for the QA tool. */
export const ZOO_SHEET_SCENE: SceneId = ZOO_SCENE
